/**
 * Pure operations on the shared guest list: head counts, summaries, duplicates, filtering, merging an import,
 * and carrying over a list saved by the earlier version of the Guest List Tracker.
 */
import { isValidId, newId } from "./ids";
import { parseBackup } from "@/lib/backup";
import { GUEST_STATUSES, type Guest, type GuestStatus } from "./types";

export const STATUS_LABEL: Record<GuestStatus, string> = {
  invited: "Invited",
  confirmed: "Confirmed",
  declined: "Declined",
  waitlist: "Waitlist",
  attended: "Attended",
};

/** People this guest brings to the event: the guest plus their extra people, for guests who are coming. */
export function partySize(g: Guest): number {
  return g.status === "confirmed" || g.status === "attended" ? 1 + (g.plusOnes ?? 0) : 0;
}

export interface GuestSummary {
  total: number;
  byStatus: Record<GuestStatus, number>;
  /** Everyone coming: confirmed and attended guests plus their extra people. */
  headcount: number;
  needsStay: number;
  withDietary: number;
  withAccess: number;
}

export function summarizeGuests(guests: readonly Guest[]): GuestSummary {
  const byStatus = Object.fromEntries(GUEST_STATUSES.map((s) => [s, 0])) as Record<GuestStatus, number>;
  let headcount = 0, needsStay = 0, withDietary = 0, withAccess = 0;
  for (const g of guests) {
    byStatus[g.status]++;
    headcount += partySize(g);
    if (g.needsStay) needsStay++;
    if (g.dietary) withDietary++;
    if (g.access) withAccess++;
  }
  return { total: guests.length, byStatus, headcount, needsStay, withDietary, withAccess };
}

const squash = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/** The identity used to spot the same person twice: their email when they have one, otherwise their name. */
export function guestKey(g: Guest): string {
  return g.email ? `email:${g.email.toLowerCase()}` : `name:${squash(g.name)}`;
}

/** Groups of two or more guests that look like the same person. */
export function findDuplicateGuests(guests: readonly Guest[]): Guest[][] {
  const groups = new Map<string, Guest[]>();
  for (const g of guests) {
    const k = guestKey(g);
    groups.set(k, [...(groups.get(k) ?? []), g]);
  }
  return Array.from(groups.values()).filter((g) => g.length > 1);
}

/** Keeps the first of each group of duplicates. */
export function removeDuplicateGuests(guests: readonly Guest[]): { guests: Guest[]; removed: number } {
  const seen = new Set<string>();
  const out: Guest[] = [];
  for (const g of guests) {
    const k = guestKey(g);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(g);
  }
  return { guests: out, removed: guests.length - out.length };
}

export interface GuestFilter { text?: string; status?: GuestStatus | "all"; needsStay?: boolean }

export function filterGuests(guests: readonly Guest[], f: GuestFilter): Guest[] {
  const q = f.text ? squash(f.text) : "";
  return guests.filter((g) => {
    if (f.status && f.status !== "all" && g.status !== f.status) return false;
    if (f.needsStay && !g.needsStay) return false;
    if (!q) return true;
    return [g.name, g.email, g.organisation, g.role, g.dietary, g.access, g.table, g.notes].some((v) => v && squash(v).includes(q));
  });
}

/** Adds imported guests to the list. An imported id that is already in use gets a new one, so nothing is overwritten. */
export function mergeImportedGuests(existing: readonly Guest[], imported: readonly Guest[]): Guest[] {
  const used = new Set(existing.map((g) => g.id));
  const added = imported.map((g) => {
    let id = g.id;
    while (used.has(id)) id = newId("g");
    used.add(id);
    return id === g.id ? g : { ...g, id };
  });
  return [...existing, ...added];
}

const LEGACY_STATUS: Record<string, { status: GuestStatus; note?: string }> = {
  Pending: { status: "invited" },
  Yes: { status: "confirmed" },
  No: { status: "declined" },
  Maybe: { status: "invited", note: "Replied maybe" },
};

/**
 * Turns the list kept by the first version of the tracker (numeric ids, Pending/Yes/No/Maybe, plus-ones as text)
 * into shared guests. Anything unreadable is skipped.
 */
export function migrateLegacyGuests(raw: unknown): Guest[] {
  if (!Array.isArray(raw)) return [];
  const out: Guest[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const r = item as Record<string, unknown>;
    const name = typeof r.name === "string" ? r.name.trim().slice(0, 300) : "";
    if (!name) continue;
    const mapped = LEGACY_STATUS[typeof r.status === "string" ? r.status : ""] ?? { status: "invited" as GuestStatus };
    const plus = parseInt(typeof r.plusOnes === "string" || typeof r.plusOnes === "number" ? String(r.plusOnes) : "0", 10);
    const guest: Guest = { id: newId("g"), name, status: mapped.status, needsStay: false };
    if (Number.isFinite(plus) && plus > 0) guest.plusOnes = Math.min(plus, 1000);
    if (mapped.note) guest.notes = mapped.note;
    out.push(guest);
  }
  return out;
}

export const isGuestId = isValidId;

/** Reads a backup file saved by the earlier Guest List Tracker ("Save backup") and returns its guests in the shared format. */
export function guestsFromLegacyBackup(text: string): { ok: true; guests: Guest[] } | { ok: false; error: string } {
  const r = parseBackup(text, "guest-list-tracker", ["fdp-GuestListTracker-"]);
  if (!r.ok) return r;
  const guests = migrateLegacyGuests(r.entries["fdp-GuestListTracker-guests-v1"]);
  return guests.length ? { ok: true, guests } : { ok: false, error: "That backup has no guests in it." };
}
